import React, { useCallback, useEffect, useState } from "react";
import { Save, Truck, Loader2, CheckCircle2, AlertCircle, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import JubelioRegionPicker from "@/components/JubelioRegionPicker";
import LocationPicker from "@/components/LocationPicker";
import CourierSettingsPanel from "@/components/admin/CourierSettingsPanel";
import ShippingModePanel from "@/components/admin/ShippingModePanel";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";

const PROVIDER_MOCK = { value: "mock", label: "Mock (data simulasi — default)", desc: "Tarif kurir dummy deterministic. Aman untuk dev/staging." };
const PROVIDER_JUBELIO = { value: "jubelio", label: "Jubelio Shipment (v1.8)", desc: "Real-time rates dari Jubelio API. Wajib set Area Gudang + kredensial OAuth di /admin/jubelio." };

const EMPTY_ORIGIN = {
  name: "", phone: "", email: "", address: "",
  province: "", city: "", district: "", subdistrict: "", postal_code: "",
  province_id: "", city_id: "", district_id: "", area_id: "",
  coordinate: "",
};

const EMPTY_FLAT = { code: "", label: "", cost: 0, eta: "" };

export default function ShippingSettings() {
  const jubelioEnabled = useIsFeatureEnabled("integration.jubelio");
  const PROVIDERS = jubelioEnabled ? [PROVIDER_MOCK, PROVIDER_JUBELIO] : [PROVIDER_MOCK];
  const [origin, setOrigin] = useState(EMPTY_ORIGIN);
  const [provider, setProvider] = useState("mock");
  const [serviceCategories, setServiceCategories] = useState([]);
  const [defaultServiceCategoryId, setDefaultServiceCategoryId] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [manualFlatEnabled, setManualFlatEnabled] = useState(false);
  const [manualFlatRates, setManualFlatRates] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState(null);

  const load = useCallback(async () => {
    setInitialLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get("/admin/settings");
      const w = data?.warehouse_origin || {};
      setOrigin({ ...EMPTY_ORIGIN, ...w });
      const sh = data?.shipping || {};
      // FEATURE GATE (integration.jubelio): downgrade otomatis ke mock kalau
      // tenant tidak berhak. Backend juga menolak jubelio → double-defense.
      const rawProvider = sh.provider === "jubelio" ? "jubelio" : "mock";
      setProvider(jubelioEnabled ? rawProvider : "mock");
      setDefaultServiceCategoryId(sh.default_service_category_id || "");
      setManualFlatEnabled(!!sh.manual_flat_enabled);
      setManualFlatRates(Array.isArray(sh.manual_flat_rates) ? sh.manual_flat_rates : []);
    } catch (e) {
      if (e?.response?.status !== 401) setLoadError(formatApiError(e));
    } finally {
      setInitialLoading(false);
    }
  }, [jubelioEnabled]);

  useEffect(() => { load(); }, [load]);

  const loadCategories = useCallback(async () => {
    setCatLoading(true); setCatError(null);
    try {
      const { data } = await api.get("/admin/jubelio/services/categories");
      setServiceCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setCatError(e?.response?.data?.detail || formatApiError(e));
    } finally { setCatLoading(false); }
  }, []);

  useEffect(() => {
    if (provider === "jubelio" && jubelioEnabled && !catLoading && serviceCategories.length === 0 && !catError) {
      loadCategories();
    }
  }, [provider, jubelioEnabled, catLoading, catError, serviceCategories.length, loadCategories]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        warehouse_origin: origin,
        shipping: {
          provider,
          default_service_category_id: defaultServiceCategoryId || null,
          manual_flat_enabled: manualFlatEnabled,
          manual_flat_rates: manualFlatRates,
        },
      });
      toast.success("Shipping settings berhasil disimpan");
      await load();
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setOrigin((o) => ({ ...o, [k]: v }));

  const addFlatRate = () => setManualFlatRates((arr) => [...arr, { ...EMPTY_FLAT }]);
  const updFlatRate = (i, k, v) => setManualFlatRates((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delFlatRate = (i) => setManualFlatRates((arr) => arr.filter((_, idx) => idx !== i));

  return (
    <AdminLayout title="Shipping Settings">
      <div className="max-w-3xl space-y-5" data-testid="admin-shipping-settings">
        <Card className="p-4 bg-blue-50 border border-blue-200 flex gap-3 text-sm text-blue-900">
          <Truck size={18} className="shrink-0 mt-0.5" />
          <div>
            Konfigurasi origin warehouse + kategori layanan + fallback tarif flat. Dipakai endpoint <code>POST /api/shipping/rates</code> di checkout.
            Mode <strong>Jubelio v1.8</strong> butuh <code>area_id</code> warehouse (set di section &quot;Alamat Gudang&quot; di bawah).
          </div>
        </Card>

        {loadError && (
          <Card className="p-4 bg-red-50 border border-red-200 flex gap-3 text-sm text-red-900" data-testid="shipping-load-error">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="flex-1">{loadError}</div>
            <Button size="sm" variant="outline" onClick={load} className="rounded-lg">Coba lagi</Button>
          </Card>
        )}

        {initialLoading ? (
          <Card className="p-8 flex items-center justify-center gap-2 text-sm text-[#5C5C5C]" data-testid="shipping-loading">
            <Loader2 size={16} className="animate-spin" /> Memuat data...
          </Card>
        ) : (
          <>
            <Card className="p-5 space-y-3">
              <h2 className="text-lg font-semibold">Shipping Provider</h2>
              <div className="space-y-2">
                {PROVIDERS.map((p) => (
                  <label key={p.value} className={`flex gap-3 p-3 rounded-xl border cursor-pointer ${provider === p.value ? "border-[#E85D04] bg-[#FFEDD5]/30" : "border-[#E5E5E5]"}`} data-testid={`provider-${p.value}`}>
                    <input type="radio" name="provider" value={p.value} checked={provider === p.value} onChange={() => setProvider(p.value)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-bold flex items-center gap-2">
                        {p.label}
                        {provider === p.value && <CheckCircle2 size={14} className="text-[#E85D04]" />}
                      </div>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Card>

            {provider === "jubelio" && jubelioEnabled && (
              <Card className="p-5 space-y-3" data-testid="jubelio-category-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">Kategori Layanan Default (Jubelio)</h2>
                  <Button size="sm" variant="outline" onClick={loadCategories} disabled={catLoading} className="rounded-lg text-xs" data-testid="reload-categories-btn">
                    <RefreshCw size={12} className={`mr-1 ${catLoading ? "animate-spin" : ""}`} /> Refresh
                  </Button>
                </div>
                <p className="text-xs text-[#5C5C5C]">Kategori default dipakai di checkout kalau customer tidak pilih. Kosongkan untuk dapat <code>/rates/all</code> (semua kategori).</p>
                {catError && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">{catError}</div>}
                <select
                  value={defaultServiceCategoryId}
                  onChange={(e) => setDefaultServiceCategoryId(e.target.value)}
                  className="w-full border border-[#E5E5E5] rounded-lg p-2 text-sm bg-white"
                  data-testid="default-service-category-select"
                >
                  <option value="">— Semua kategori (panggil /rates/all) —</option>
                  {serviceCategories.map((c) => (
                    <option key={c.service_category_id} value={c.service_category_id}>
                      {c.name} (id: {c.service_category_id})
                    </option>
                  ))}
                </select>
              </Card>
            )}

            <Card className="p-5 space-y-4">
              <h2 className="text-lg font-semibold">Alamat Gudang (Origin)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Nama Gudang</Label><Input value={origin.name || ""} onChange={(e) => upd("name", e.target.value)} placeholder="Gudang Pusat" data-testid="warehouse-name" /></div>
                <div><Label>Telepon</Label><Input value={origin.phone || ""} onChange={(e) => upd("phone", e.target.value)} placeholder="08123456789" data-testid="warehouse-phone" /></div>
                <div className="md:col-span-2"><Label>Email (opsional)</Label><Input value={origin.email || ""} onChange={(e) => upd("email", e.target.value)} placeholder="gudang@tokoanda.com" data-testid="warehouse-email" /></div>
                <div className="md:col-span-2"><Label>Alamat Lengkap</Label><Input value={origin.address || ""} onChange={(e) => upd("address", e.target.value)} placeholder="Jl. ... No. ..." data-testid="warehouse-address" /></div>
              </div>

              {provider === "jubelio" && jubelioEnabled ? (
                <div className="space-y-2 mt-2">
                  <p className="text-xs font-bold text-[#7A5440] uppercase tracking-wider">Region Jubelio (untuk dapatkan area_id)</p>
                  <JubelioRegionPicker
                    value={origin}
                    onChange={(v) => setOrigin((o) => ({ ...o, ...v }))}
                    adminMode
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Provinsi</Label><Input value={origin.province || ""} onChange={(e) => upd("province", e.target.value)} placeholder="DKI Jakarta" data-testid="warehouse-province" /></div>
                  <div><Label>Kota</Label><Input value={origin.city || ""} onChange={(e) => upd("city", e.target.value)} placeholder="Jakarta Selatan" data-testid="warehouse-city" /></div>
                  <div><Label>Kecamatan</Label><Input value={origin.district || ""} onChange={(e) => upd("district", e.target.value)} placeholder="Kebayoran Baru" data-testid="warehouse-district" /></div>
                  <div><Label>Kelurahan</Label><Input value={origin.subdistrict || ""} onChange={(e) => upd("subdistrict", e.target.value)} placeholder="Senayan" data-testid="warehouse-subdistrict" /></div>
                  <div><Label>Kode Pos</Label><Input value={origin.postal_code || ""} onChange={(e) => upd("postal_code", e.target.value)} placeholder="12190" data-testid="warehouse-postal" /></div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label>Koordinat (Latitude/Longitude)</Label>
                  <button
                    type="button"
                    onClick={() => setPinOpen(true)}
                    className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded brand-btn-primary transition"
                    data-testid="warehouse-pick-pin-btn"
                  >
                    {origin.coordinate ? "Ubah Pin Lokasi" : "Pilih Pin Lokasi"}
                  </button>
                </div>
                <Input value={origin.coordinate || ""} onChange={(e) => upd("coordinate", e.target.value)} placeholder="-6.212,106.821" data-testid="warehouse-coordinate" />
                <p className="text-[10px] text-[#5C5C5C] mt-1">
                  Format: <code>lat,lng</code>. <b className="text-amber-700">WAJIB untuk kurir instant</b> (GoSend/Grab/Paxel). Bisa pilih dari map picker.
                </p>
              </div>
            </Card>

            {pinOpen && (
              <Dialog open={pinOpen} onOpenChange={setPinOpen}>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Pilih Pin Lokasi Gudang</DialogTitle>
                    <DialogDescription className="sr-only">
                      Pilih titik lokasi tepat gudang origin dari peta.
                    </DialogDescription>
                  </DialogHeader>
                  <LocationPicker
                    value={origin.coordinate || ""}
                    onChange={(v) => upd("coordinate", v)}
                    showCoords
                  />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPinOpen(false)} className="rounded-xl" data-testid="warehouse-pin-cancel">Batal</Button>
                    <Button onClick={() => setPinOpen(false)} className="brand-btn-primary rounded-xl" data-testid="warehouse-pin-save">Simpan Pin</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            <Card className="p-5 space-y-3" data-testid="manual-flat-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-lg font-semibold">Fallback Tarif Flat (Manual)</h2>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={manualFlatEnabled} onChange={(e) => setManualFlatEnabled(e.target.checked)} data-testid="manual-flat-enabled-toggle" />
                  Aktifkan
                </label>
              </div>
              <p className="text-xs text-[#5C5C5C]">
                Dipakai sebagai fallback kalau Jubelio API error / area_id customer kosong.
                Customer tetap bisa checkout dengan tarif flat ini.
              </p>
              {manualFlatRates.length === 0 && (
                <div className="text-xs text-[#5C5C5C] text-center py-3 border border-dashed border-[#E5E5E5] rounded-lg">Belum ada tarif flat. Klik &quot;Tambah Tarif&quot; untuk mulai.</div>
              )}
              {manualFlatRates.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`flat-rate-row-${i}`}>
                  <div className="col-span-3"><Label className="text-[10px]">Kode</Label><Input value={r.code || ""} onChange={(e) => updFlatRate(i, "code", e.target.value)} placeholder="REG" data-testid={`flat-code-${i}`} /></div>
                  <div className="col-span-4"><Label className="text-[10px]">Nama Layanan</Label><Input value={r.label || ""} onChange={(e) => updFlatRate(i, "label", e.target.value)} placeholder="Reguler (1-3 hari)" data-testid={`flat-label-${i}`} /></div>
                  <div className="col-span-2"><Label className="text-[10px]">Tarif (Rp)</Label><Input type="number" value={r.cost || 0} onChange={(e) => updFlatRate(i, "cost", Number(e.target.value) || 0)} data-testid={`flat-cost-${i}`} /></div>
                  <div className="col-span-2"><Label className="text-[10px]">ETA</Label><Input value={r.eta || ""} onChange={(e) => updFlatRate(i, "eta", e.target.value)} placeholder="1-3 hari" data-testid={`flat-eta-${i}`} /></div>
                  <div className="col-span-1"><Button size="sm" variant="outline" onClick={() => delFlatRate(i)} className="text-[#DC2F02] rounded-lg" data-testid={`flat-del-${i}`}><Trash2 size={12} /></Button></div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addFlatRate} className="rounded-lg" data-testid="add-flat-rate-btn"><Plus size={12} className="mr-1" /> Tambah Tarif Flat</Button>
            </Card>

            {/* Shipping Mode (sprint UX cleanup): realtime vs flat */}
            <ShippingModePanel />

            {/* Shipping Settings v2: kontrol kurir aktif/prioritas/rekomendasi */}
            <CourierSettingsPanel />

            <div className="flex gap-2 sticky bottom-2 bg-white/90 backdrop-blur p-2 rounded-xl border border-[#E5E5E5]">
              <Button onClick={save} disabled={saving} className="bg-[#4A2E1F] hover:bg-[#3a2417] text-white rounded-xl" data-testid="shipping-save-btn">
                <Save size={16} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Shipping Settings"}
              </Button>
              <Button onClick={load} variant="outline" className="rounded-xl" disabled={saving} data-testid="shipping-reload-btn">Reload</Button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
